import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, KeyRound, LogIn, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { AuthHero, KlysticLogo } from "@/components/KlysticBrand";

function formatErr(err) {
  if (err && !err.response) {
    return `Network error — cannot reach ${API}. Check that the backend URL is correct and DNS is live.`;
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const { signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [signInCode, setSignInCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.resetPassword({ email, code, new_password: newPassword });
      toast.success("Password updated. Enter a fresh code to sign in.");
      setDone(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const signInNow = async (e) => {
    e.preventDefault();
    if (signInCode.length !== 6) return;
    setBusy(true);
    try {
      await signIn({ email, password: newPassword, code: signInCode });
      toast.success("Signed in");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      <AuthHero
        overline="Forgot password?"
        title="Reset it with"
        highlight="your authenticator."
        description={
          <>
            Your 6-digit <strong>Klystic</strong> code acts as proof it&apos;s really you.
            Enter the current code from your Authenticator app, then choose a new
            password.
          </>
        }
      >
        <ul className="space-y-3">
          <li className="flex items-center gap-2">
            <Smartphone size={14} className="text-klystic-green" /> Needs an enrolled authenticator
          </li>
          <li className="flex items-center gap-2">
            <KeyRound size={14} className="text-klystic-green" /> Lost your phone?{" "}
            <Link to="/reset-2fa" className="underline text-klystic-sky">
              Reset 2FA
            </Link>
          </li>
        </ul>
      </AuthHero>

      <div className="lg:col-span-3 flex items-center justify-center p-8">
        {!done ? (
          <form
            onSubmit={submit}
            data-testid="reset-password-form"
            className="w-full max-w-md bg-white border border-slate-200 p-8"
          >
            <KlysticLogo className="lg:hidden mb-6" label="Recovery" />
            <div className="overline text-klystic-sky">Reset password</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-klystic-ink mt-1">
              Set a new password
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Enter your email, your current 6-digit code, and the new password you want
              to use.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label className="overline text-slate-500">Email</Label>
                <Input
                  data-testid="rp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
              <div>
                <Label className="overline text-slate-500 flex items-center gap-2">
                  <Smartphone size={12} /> Current 6-digit code
                </Label>
                <Input
                  data-testid="rp-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  className="rounded-none border-slate-300 font-mono text-center text-2xl tracking-[0.5em] focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
              <div>
                <Label className="overline text-slate-500">New password (8+ chars)</Label>
                <Input
                  data-testid="rp-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
              <div>
                <Label className="overline text-slate-500">Confirm new password</Label>
                <Input
                  data-testid="rp-confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="rp-submit"
              className="w-full mt-6 bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-11"
            >
              <KeyRound size={16} className="mr-2" />
              {busy ? "Updating…" : "Update password"}
              <ArrowRight size={16} className="ml-2" />
            </Button>

            <div className="mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500 text-center">
              <Link
                to="/login"
                data-testid="rp-to-login"
                className="text-klystic-sky font-medium hover:text-klystic-sky-dark"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={signInNow}
            data-testid="rp-signin-form"
            className="w-full max-w-md bg-white border border-slate-200 p-8"
          >
            <div className="overline text-klystic-sky">Password updated</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-klystic-ink mt-1">
              You&apos;re all set
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Enter a <strong>fresh</strong> 6-digit code (wait for your Authenticator to
              rotate if needed) and we&apos;ll sign you in.
            </p>

            <div className="mt-6">
              <Label className="overline text-slate-500 flex items-center gap-2">
                <Smartphone size={12} /> Fresh 6-digit code
              </Label>
              <Input
                data-testid="rp-signin-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                value={signInCode}
                onChange={(e) => setSignInCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="rounded-none border-slate-300 font-mono text-center text-2xl tracking-[0.5em] focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
              />
            </div>

            <Button
              type="submit"
              disabled={busy || signInCode.length !== 6}
              data-testid="rp-signin-submit"
              className="w-full mt-6 bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-11"
            >
              <LogIn size={16} className="mr-2" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
